import { httpServices } from './client';

/**
 * The composer draft of the signed-in user in one Q&A
 * (`/llm/chat/inquiry/{id}/draft`). Contract:
 * rakuyaku-app-api/docs/v2/preferences-and-export-api.md §4.
 */

export interface StoredDraft {
  inquiry_id: number;
  text: string | null;
  attachment_ids: number[];
  updated_at: string | null;
  /**
   * When the stored text was typed, on the client's clock that saved it
   * (API prompt 17); null when unknown or from an older API.
   */
  client_saved_at?: string | null;
  /** When a send last cleared the draft (the send's client_sent_at), or null. */
  cleared_at: string | null;
}

interface DraftResponse {
  success: boolean;
  data?: StoredDraft | null;
  code?: string;
  status?: number;
  body?: { cleared_at?: string | null };
}

export type DraftRead =
  | { kind: 'ok'; draft: StoredDraft | null }
  | { kind: 'failed' };

/** GET …/draft; `draft` is null when there is none. */
export const getDraft = async (inquiryId: string): Promise<DraftRead> => {
  const response = await httpServices.llmService.get<DraftResponse>(
    `/chat/inquiry/${inquiryId}/draft`,
  );
  if (!response?.success) return { kind: 'failed' };
  return { kind: 'ok', draft: response.data ?? null };
};

export type DraftWrite =
  | { kind: 'saved'; draft: StoredDraft | null }
  /** 409 draft_stale: the text was sent after this save was typed. */
  | { kind: 'stale'; clearedAt: string | null }
  | { kind: 'failed'; status: number };

/**
 * PUT …/draft. `clientSavedAt` is when the text was typed (not when the
 * debounced request leaves), from the same clock as the send's
 * `client_sent_at`, so a save that arrives after the send is refused.
 */
export const putDraft = async (
  inquiryId: string,
  text: string | null,
  clientSavedAt: string,
): Promise<DraftWrite> => {
  const response = await httpServices.llmService.put<DraftResponse>(
    `/chat/inquiry/${inquiryId}/draft`,
    { text, client_saved_at: clientSavedAt },
  );
  if (response?.success) return { kind: 'saved', draft: response.data ?? null };
  if (response?.code === 'draft_stale') {
    return { kind: 'stale', clearedAt: response.body?.cleared_at ?? null };
  }
  return { kind: 'failed', status: Number(response?.status ?? 0) };
};
